/**
 *
 * AchievementDetails
 *
 */

import React, { memo, FunctionComponent, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';
import { Achievement, AchievementStatus } from '../../achievements';
import {
  ComponentDialogBody,
  StyledDialogActionsBar,
  StyledModalAction,
} from '../../../../components/DiaogWrap/styles';
import { Column, Row, Wrap } from '../../../../assets/styledAssets/grid';
import {
  StyledDatePicker,
  StyledDropDownList,
  StyledFormField,
  StyledLabel,
} from '../../../../components/FormField/styles';
import { Input, MaskedTextBox } from '@progress/kendo-react-inputs';
import { fontColorSecond } from '../../../../assets/styledAssets/vars';
import Button from '../../../../components/Button/Button.component';
import AchievementsService from '../../../../service/Achievements.service';
import {
  WorldRank,
  WorldRecord,
  WorldRecordType,
} from '../../../WorldRecords/worldrecords';
import moment from 'moment';
import {
  convertFromTimeMaskToSec,
  convertToTimeMask,
} from '../../../../helpers/app.helpers';

interface IProps {
  achievement?: Achievement;
  onCancel: () => void;
  onSave: (a: Achievement) => void;
  onDelete?: (id: string) => void;
}

const rankValue = [
  { id: WorldRank.Recreation, name: 'Recreation' },
  { id: WorldRank.Junior, name: 'Junior' },
  { id: WorldRank.Regional, name: 'Regional' },
  { id: WorldRank.National, name: 'National' },
  { id: WorldRank.International, name: 'International' },
];

const AchievementDetails: FunctionComponent<IProps> = ({
  achievement,
  onCancel,
  onSave,
  onDelete,
}) => {
  const intl = useIntl();
  const [list, setList] = useState<WorldRecord[]>([]);
  const [email, setEmail] = useState<string>('');
  const [details, setDetails] = useState<Partial<Achievement>>(
    achievement || { achievedOrStart: new Date() },
  );

  useEffect(() => {
    AchievementsService.fetchWorldRecords().then((res) => setList(res.items));
  }, []);

  const itemFromValue = (value: any) => {
    return list.find((item) => item.eventId === value);
  };

  const createRequest = () => {
    AchievementsService.createAchievementRequest(
      details?.id as string,
      email,
    ).then(() => {
      setEmail('');
      onCancel();
    });
  };

  return (
    <>
      <ComponentDialogBody>
        <Row>
          <Wrap margin={'0 0 3rem'} width={'100%'}>
            <Column xs={100} md={100}>
              <StyledLabel>{intl.formatMessage(messages.event)}</StyledLabel>
              <StyledDropDownList
                data={list}
                textField={'event.name'}
                dataItemKey={'eventId'}
                value={itemFromValue(details?.eventId)}
                onChange={(e) =>
                  setDetails({
                    ...details,
                    eventId: e.value.eventId,
                    event: e.value.event,
                  })
                }
              />
            </Column>
          </Wrap>
          <Wrap margin={'0 0 3rem'} width={'100%'}>
            {details?.event?.input === WorldRecordType.Rank && (
              <Column xs={100} md={100} padding={'1.5rem'}>
                <StyledLabel>{intl.formatMessage(messages.result)}</StyledLabel>
                <StyledDropDownList
                  data={rankValue}
                  textField={'name'}
                  dataItemKey={'id'}
                  value={rankValue.find(
                    (r) =>
                      (r.id as any) === parseInt(details?.levelOrResult || ''),
                  )}
                  onChange={(e) =>
                    setDetails({
                      ...details,
                      levelOrResult: e.value.id,
                    })
                  }
                />
              </Column>
            )}
            {details?.event?.input === WorldRecordType.Time && (
              <Column xs={100} md={100} padding={'1.5rem'}>
                <StyledLabel>{intl.formatMessage(messages.result)}</StyledLabel>
                <StyledFormField>
                  <MaskedTextBox
                    mask="00:00:00.00"
                    onChange={(e) =>
                      setDetails({
                        ...details,
                        levelOrResult: convertFromTimeMaskToSec(
                          e.target.value,
                        ) as any,
                      })
                    }
                    value={convertToTimeMask(details?.levelOrResult || 0)}
                  />
                </StyledFormField>
              </Column>
            )}
            {![WorldRecordType.Rank, WorldRecordType.Time].includes(
              details?.event?.input as WorldRecordType,
            ) && (
              <Column xs={100} md={100}>
                <StyledLabel>{intl.formatMessage(messages.result)}</StyledLabel>
                <StyledFormField>
                  <Input
                    onChange={(e) =>
                      setDetails({
                        ...details,
                        levelOrResult: e.value,
                      })
                    }
                    value={details?.levelOrResult}
                  />
                </StyledFormField>
              </Column>
            )}
          </Wrap>
          <Wrap width={'100%'}>
            <Column xs={100} md={100}>
              <StyledLabel>
                {intl.formatMessage(messages.startDate)}
              </StyledLabel>
              <StyledDatePicker
                onChange={(e) =>
                  setDetails({ ...details, achievedOrStart: e.value as Date })
                }
                value={moment(details?.achievedOrStart || new Date()).toDate()}
              />
            </Column>
          </Wrap>
          {!!details.id && details.status === AchievementStatus.Pending && (
            <Wrap margin={'0 0 4rem'} width={'100%'}>
              <Wrap
                margin={'4rem 0 0'}
                width={'100%'}
                borders={[{ side: 'bottom', color: fontColorSecond }]}
              />
              <Column xs={100} md={100}>
                <Wrap margin={'4rem 0'} width={'100%'}>
                  <Wrap fs={'2.4rem'} fw={'bold'}>
                    {intl.formatMessage(messages.requestConfirmation)}
                  </Wrap>
                </Wrap>
                <StyledLabel>{intl.formatMessage(messages.email)}</StyledLabel>
                <StyledFormField>
                  <Input onChange={(e) => setEmail(e.value)} value={email} />
                </StyledFormField>
                <Wrap margin={'3rem 0'} width={'100%'}>
                  <Button disabled={!email} onClick={createRequest}>
                    {intl.formatMessage(messages.sendRequest)}
                  </Button>
                </Wrap>
              </Column>
            </Wrap>
          )}
        </Row>
      </ComponentDialogBody>
      <StyledDialogActionsBar>
        {details && details.id && onDelete && (
          <StyledModalAction
            primary
            onClick={() => {
              onDelete(details.id as string);
              onCancel();
            }}
          >
            {intl.formatMessage(messages.delete)}
          </StyledModalAction>
        )}

        <StyledModalAction onClick={onCancel}>
          {intl.formatMessage(messages.cancel)}
        </StyledModalAction>
        <StyledModalAction
          accent
          onClick={() => onSave(details as Achievement)}
        >
          {intl.formatMessage(messages.save)}
        </StyledModalAction>
      </StyledDialogActionsBar>
    </>
  );
};

export default memo(AchievementDetails);
